import { useEffect, useState } from 'react';
import type { CogniSummary, CogniSummaryCite } from '../../types';
import type { CogniSummaryState } from '../../hooks/useCogniSummary';
import { citeLabel, quotedExamples } from '../../lib/cogniSummary';

interface CogniSummaryPanelProps {
  state: CogniSummaryState;
  onGenerate: () => void;
  onHide: () => void;
  onCiteClick: (c: CogniSummaryCite) => void;
  onContinueInInvestigator: () => void;
}

const linkBtn = 'text-[13px] text-accent hover:underline';

function SummaryCard({
  summary,
  onHide,
  onCiteClick,
  onContinueInInvestigator,
}: {
  summary: CogniSummary;
  onHide: () => void;
  onCiteClick: (c: CogniSummaryCite) => void;
  onContinueInInvestigator: () => void;
}) {
  const [showExamples, setShowExamples] = useState(false);
  useEffect(() => setShowExamples(false), [summary]);
  const examples = quotedExamples(summary);
  return (
    <section aria-label="Summary" className="bg-white border border-line rounded p-6 flex flex-col gap-3.5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="eyebrow">AI summary · from the top 10 results</span>
        <button type="button" onClick={onHide} className="text-[13px] text-ink-2 hover:underline">
          Hide summary
        </button>
      </div>
      <h2 className="font-display text-xl font-medium tracking-tight text-ink">{summary.title}</h2>
      <ol className="flex flex-col gap-3 list-none p-0 m-0">
        {summary.patterns.map((p, i) => (
          <li key={i} className="grid grid-cols-[28px_minmax(0,1fr)] gap-2 text-sm leading-relaxed">
            <span className="font-mono text-[13px] text-ink-2">{String(i + 1).padStart(2, '0')}</span>
            <span>
              {p.text}{' '}
              {p.cites.map((c, j) => (
                <button
                  key={`${c.ref}-${j}`}
                  type="button"
                  onClick={() => onCiteClick(c)}
                  className="font-mono text-[11px] px-[5px] py-px mr-1 border border-accent-ring rounded-sm text-ink hover:bg-accent-tint"
                >
                  {citeLabel(c)}
                </button>
              ))}
            </span>
          </li>
        ))}
      </ol>
      {showExamples && (
        <ul aria-label="Quoted examples" className="flex flex-col gap-2 list-none p-0 m-0 text-[13px] text-ink-2">
          {examples.map((c) => (
            <li key={c.drug_id}>
              <span className="font-semibold text-ink">{c.drug_name}</span>{' '}
              <span className="font-mono text-[11px]">{c.section_number ?? c.section_title}</span>
              <blockquote className="m-0 mt-0.5">{c.quote}</blockquote>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-4 pt-1 border-t border-line-subtle">
        <button
          type="button"
          aria-expanded={showExamples}
          onClick={() => setShowExamples((v) => !v)}
          className={`mt-2.5 ${linkBtn}`}
        >
          Show quoted examples ({examples.length})
        </button>
        <button type="button" onClick={onContinueInInvestigator} className={`mt-2.5 ${linkBtn}`}>
          Continue in Investigator
        </button>
      </div>
    </section>
  );
}

/** On-request AI summary: dashed CTA card, or the summary card when open. */
export default function CogniSummaryPanel({
  state,
  onGenerate,
  onHide,
  onCiteClick,
  onContinueInInvestigator,
}: CogniSummaryPanelProps) {
  if (state.phase === 'ready' && state.open) {
    return (
      <SummaryCard
        summary={state.summary}
        onHide={onHide}
        onCiteClick={onCiteClick}
        onContinueInInvestigator={onContinueInInvestigator}
      />
    );
  }
  const loading = state.phase === 'loading';
  const retry = state.phase === 'error' && state.retry;
  return (
    <section
      aria-label="AI summary"
      className="flex flex-wrap items-center justify-between gap-3 py-3.5 pl-5 pr-4 bg-white border border-dashed border-line-input rounded"
    >
      <div className="flex flex-col gap-0.5">
        <span className="font-semibold text-ink">AI summary of the top 10 results</span>
        <span className="text-[13px] text-ink-2">
          Optional. Pulls the common patterns out of the top results, with every point linked to its label section.
        </span>
        {state.phase === 'error' && (
          <span role="alert" className="text-[13px] text-signal mt-1">
            {state.message}
          </span>
        )}
      </div>
      <button type="button" onClick={onGenerate} disabled={loading} className="btn-secondary">
        {loading ? 'Generating…' : retry ? 'Try again' : 'Generate summary'}
      </button>
    </section>
  );
}
